import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Shuffle, MessageCircle, Sparkles, Mic } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import VerseCard from "../components/VerseCard";
import ChatPanel from "../components/ChatPanel";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { useVoiceGuide } from "../lib/useVoice";
import { recordVisit, getVerseOfDay, getCanon } from "../api/client";

export default function Home() {
  const { lang, deviceId } = useApp();
  const navigate = useNavigate();
  const { startCall } = useVoiceGuide();
  const [verse, setVerse] = useState(null);
  const [count, setCount] = useState(null);
  const [showChat, setShowChat] = useState(false);

  const speakVoice = () => {
    const launched = startCall();
    if (!launched) toast.info(t(lang, "voiceOffline"), { duration: 6000 });
  };

  useEffect(() => {
    recordVisit(deviceId).then((d) => setCount(d.today));
  }, [deviceId]);

  useEffect(() => {
    getVerseOfDay(lang).then(setVerse);
  }, [lang]);

  const openRandom = async () => {
    const canon = await getCanon(lang);
    const books = canon.books || [];
    if (!books.length) return;
    const b = books[Math.floor(Math.random() * books.length)];
    const ch = Math.max(1, Math.floor(Math.random() * (b.numberOfChapters || 1)) + 1);
    navigate(`/read/${b.id}/${ch}`);
  };

  return (
    <div className="relative z-10 min-h-screen flex flex-col items-center px-4 py-10 md:py-16">
      <header className="text-center fade-up">
        <Logo size="lg" />
        <p className="mt-3 text-lg md:text-xl text-[var(--ink-soft)] font-scripture" style={{ textShadow: "0 1px 10px rgba(255,255,255,0.55)" }}>{t(lang, "tagline")}</p>
        <div className="mt-4" style={{ textShadow: "0 1px 10px rgba(255,255,255,0.55)" }}>
          <LanguageSwitcher />
        </div>
      </header>

      {/* CTAs */}
      <div className="mt-9 flex flex-wrap items-center justify-center gap-2.5 fade-up" style={{ animationDelay: ".08s" }}>
        <Link to="/read" className="btn-ghost-gold rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2">
          <BookOpen size={16} /> {t(lang, "readBible")}
        </Link>
        <button onClick={openRandom} className="btn-ghost-gold rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2">
          <Shuffle size={16} /> {t(lang, "randomPage")}
        </button>
        <button
          onClick={() => setShowChat((v) => !v)}
          className="btn-gold rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2"
        >
          <MessageCircle size={16} /> {showChat ? t(lang, "askBelow") : t(lang, "talkAI")}
        </button>
        <button
          onClick={speakVoice}
          className="btn-gold rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2"
        >
          <Mic size={16} /> {t(lang, "speakVoice")}
        </button>
      </div>

      {count !== null && (
        <p className="mt-5 flex items-center gap-2 text-sm text-[var(--gold-deep)] fade-in">
          <Sparkles size={15} /> {t(lang, "sharedToday", count)}
        </p>
      )}

      {/* Content */}
      <main className="w-full max-w-2xl mt-8 space-y-6">
        <div className="fade-up" style={{ animationDelay: ".12s" }}>
          <VerseCard verse={verse} />
        </div>

        {showChat && (
          <div className="fade-up">
            <p className="text-center text-sm text-[var(--ink-faint)] mb-3">{t(lang, "guideIntro")}</p>
            <ChatPanel compact />
          </div>
        )}

        {/* Church family */}
        <div className="glass rounded-3xl p-6 text-center fade-up">
          <p className="text-[var(--ink-soft)]">
            {t(lang, "churchLead")}{" "}
            <a href="https://essentielmtl.com/" target="_blank" rel="noreferrer" className="font-semibold text-[var(--gold-deep)] hover:underline">
              Église Essentiæl
            </a>{" "}
            — {t(lang, "churchDesc")}
          </p>
          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-[var(--ink-faint)]">
            <a href="https://essentielmtl.com/" target="_blank" rel="noreferrer" className="hover:text-[var(--gold-deep)]">{t(lang, "visit")}</a>
            <span>·</span>
            <a href="https://essentielmtl.com/preches/" target="_blank" rel="noreferrer" className="hover:text-[var(--gold-deep)]">{t(lang, "messages")}</a>
            <span>·</span>
            <a href="https://goo.gl/maps/KF59T6pnuBx7a91t8" target="_blank" rel="noreferrer" className="hover:text-[var(--gold-deep)]">{t(lang, "sundays")}</a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full max-w-2xl mt-12 text-center text-sm text-[var(--ink-faint)] space-y-2">
        <p className="leading-relaxed">{t(lang, "footer1")}</p>
        <p>
          {t(lang, "footer2")} ·{" "}
          <Link to="/provenance" className="text-[var(--gold-deep)] hover:underline">{t(lang, "sources")}</Link>
        </p>
        <p className="pt-1">
          <Link to="/provenance" className="hover:text-[var(--gold-deep)]">{t(lang, "sourcesShort")}</Link>
          <span className="mx-2">·</span>
          <Link to="/privacy" className="hover:text-[var(--gold-deep)]">{t(lang, "privacy")}</Link>
        </p>
      </footer>
    </div>
  );
}
